import React, { useState, useEffect, useCallback, useMemo } from "react";
import apiClient from "../api/client";
import { can } from "../utils/permissions";
import { useCurrentUser } from "../hooks/useCurrentUser";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  SAFE: "תקין",
  WARNING: "אזהרה",
  BREACH: "הפרה",
  RELEASE_ELIGIBLE: "זכאי לשחרור",
  NO_COLLATERAL: "ללא בטוחה",
  NO_PRICE: "אין מחיר",
};

const PRICE_FORMULA_LABELS = {
  SPOT: "שער ידוע",
  "3DAY_AVG": "ממוצע 3י׳",
  "5DAY_AVG": "ממוצע 5י׳",
  "90DAY_AVG": "ממוצע 90י׳",
};

const GUARANTEE_TYPE_LABELS = {
  PERSONAL: "אישית",
  CORPORATE: "תאגידית",
};

// AssetHolding.assetType — badges on pledge rows + the pledge asset picker.
const ASSET_TYPE_LABELS = {
  STOCK: "מניה",
  REAL_ESTATE: 'נדל"ן',
  LAND: "קרקע",
  DEVELOPMENT_PROJECT: "פרויקט בפיתוח",
  LOAN_RECEIVABLE: "הלוואה שניתנה",
  PRIVATE_EQUITY: "הון פרטי",
  CASH: "מזומן",
  OTHER: "אחר",
};

const LIEN_POSITION_LABELS = { FIRST: "ראשון", SECOND: "שני", THIRD: "שלישי", OTHER: "אחר" };

const OVER_PLEDGED_MESSAGE = "לא ניתן לשעבד יותר מניות מסך האחזקה החופשית";
const LAST_PLEDGE_CONFIRM =
  "זהו השעבוד האחרון במסגרת — לאחר השחרור לא יהיה בסיס לחישוב LTV. להמשיך?";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatILS(value, compact = false) {
  if (value == null || value === "" || isNaN(value)) return "—";
  if (compact && Math.abs(value) >= 1_000_000) {
    return `₪${(value / 1_000_000).toFixed(1)}M`;
  }
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPct(val, decimals = 1) {
  if (val == null || isNaN(val)) return "—";
  return `${Number(val).toFixed(decimals)}%`;
}

function formatNum(val) {
  if (val == null || isNaN(val)) return "—";
  return Number(val).toLocaleString("he-IL");
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("he-IL");
}

function ltvStatusConfig(status) {
  switch (status) {
    case "BREACH":
      return { label: "הפרה", cls: "status-breach" };
    case "WARNING":
      return { label: "אזהרה", cls: "status-warning" };
    case "SAFE":
      return { label: "תקין", cls: "status-safe" };
    case "RELEASE_ELIGIBLE":
      return { label: "זכאי לשחרור", cls: "status-release" };
    case "NO_PRICE":
      return { label: "אין מחיר", cls: "status-none" };
    default:
      return { label: STATUS_LABELS[status] || "ללא בטוחה", cls: "status-none" };
  }
}

function isOverPledgedError(err) {
  return err?.response?.data?.error === "OVER_PLEDGED";
}

/** Pledge row detail string: asset name/symbol · units-or-whole · lien holder. */
function pledgeDetailText(p) {
  const asset = p.assetId || {};
  const namePart = asset.assetName || "—";
  const symbolPart = asset.symbol ? ` · ${asset.symbol}` : "";
  const unitsPart = p.pledgedUnits ? `${formatNum(p.pledgedUnits)} מניות` : "נכס בשלמותו";
  return `${namePart}${symbolPart} · ${unitsPart} · ${p.lienHolder || "—"}`;
}

/** Guarantee row detail string: type · guarantor · amount · (expiry). */
function guaranteeDetailText(g) {
  const typeLabel = GUARANTEE_TYPE_LABELS[g.guaranteeType] || g.guaranteeType || "—";
  const expiryPart = g.expiryDate ? ` · תוקף ${formatDate(g.expiryDate)}` : "";
  return `${typeLabel} · ${g.guarantorName || "—"} · ${formatILS(g.amount, true)}${expiryPart}`;
}

// ─── Mini LTV Gauge (SVG arc) ─────────────────────────────────────────────────

function MiniGauge({ ltv, warning, breach }) {
  if (ltv == null) return <span className="cp-ltv-inline status-none">—</span>;
  const cfg = ltvStatusConfig(
    ltv >= (breach || 9999) ? "BREACH" :
    ltv >= (warning || 9999) ? "WARNING" : "SAFE"
  );
  return (
    <span className={`cp-ltv-inline ${cfg.cls}`}>{formatPct(ltv)}</span>
  );
}

// ─── PledgeRow ────────────────────────────────────────────────────────────────

function PledgeRow({ pledge, canEdit, canRemove, isLastActive, onRefresh }) {
  const [releasing, setReleasing] = useState(false);
  const [editing, setEditing] = useState(false);

  const handleRelease = async () => {
    const msg = isLastActive ? LAST_PLEDGE_CONFIRM : "לשחרר שעבוד זה?";
    if (!window.confirm(msg)) return;
    setReleasing(true);
    try {
      await apiClient.delete(`/pledges/${pledge._id}`);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.error || "שחרור נכשל");
    } finally {
      setReleasing(false);
    }
  };

  const asset = pledge.assetId || {};
  const badgeLabel = ASSET_TYPE_LABELS[asset.assetType] || asset.assetType || "—";

  return (
    <>
      <div className="bt-collateral-row">
        <span className="cp-symbol-badge">{badgeLabel}</span>
        <span className="bt-collateral-detail">{pledgeDetailText(pledge)}</span>
        <div className="bt-collateral-actions">
          {canEdit && (
            <button
              type="button"
              className="ag-grid-action-btn"
              onClick={() => setEditing(true)}
            >
              עריכה
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              className="ag-grid-action-btn ag-grid-action-btn-danger"
              onClick={handleRelease}
              disabled={releasing}
            >
              {releasing ? "..." : "שחרור"}
            </button>
          )}
        </div>
      </div>
      {editing && (
        <PledgeEditModal
          pledge={pledge}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onRefresh(); }}
        />
      )}
    </>
  );
}

// ─── PledgeEditModal ──────────────────────────────────────────────────────────

function PledgeEditModal({ pledge, onClose, onSaved }) {
  const asset = pledge.assetId || {};
  const isStock = asset.assetType === "STOCK";
  const [form, setForm] = useState({
    pledgedUnits: pledge.pledgedUnits != null ? String(pledge.pledgedUnits) : "",
    lienPosition: pledge.lienPosition || "",
    lienHolder: pledge.lienHolder || "",
    notes: pledge.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      if (isStock) payload.pledgedUnits = Number(form.pledgedUnits);
      // Always send the optional fields — clearing one must persist. Backend
      // applies any !== undefined value; the enum field takes null, not "".
      payload.lienPosition = form.lienPosition || null;
      payload.lienHolder = form.lienHolder;
      payload.notes = form.notes;

      await apiClient.put(`/pledges/${pledge._id}`, payload);
      onSaved();
    } catch (err) {
      if (isOverPledgedError(err)) {
        window.alert(OVER_PLEDGED_MESSAGE);
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="cp-modal-header">
          <h3 className="cp-modal-title">עריכת שעבוד</h3>
          <button type="button" onClick={onClose} className="cp-modal-close">×</button>
        </div>
        <div className="cp-holding-reason" style={{ marginBottom: 12 }}>
          {asset.assetName || "—"}{asset.symbol ? ` · ${asset.symbol}` : ""}
        </div>
        {error && <div className="cp-error cp-modal-error">{error}</div>}
        <form onSubmit={handleSubmit} className="form-grid cp-modal-form">
          {isStock && (
            <div className="field">
              <label className="label-required">מניות לשעבוד</label>
              <input className="input" type="number" name="pledgedUnits" value={form.pledgedUnits} onChange={onChange} placeholder="0" required min="1" />
            </div>
          )}
          <div className="field">
            <label className="label-optional">דרגת שעבוד</label>
            <select className="select" name="lienPosition" value={form.lienPosition} onChange={onChange}>
              <option value="">— לא צוין —</option>
              {Object.entries(LIEN_POSITION_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="label-optional">גוף משעבד</label>
            <input className="input" name="lienHolder" value={form.lienHolder} onChange={onChange} placeholder="בנק" />
          </div>
          <div className="field form-full-width">
            <label className="label-optional">הערות</label>
            <input className="input" name="notes" value={form.notes} onChange={onChange} placeholder="הערות נוספות" />
          </div>

          <div className="form-full-width cp-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>ביטול</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "שומר..." : "עדכון"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── GuaranteeRow ─────────────────────────────────────────────────────────────

function GuaranteeRow({ guarantee, canEdit, canRemove, onRefresh }) {
  const [releasing, setReleasing] = useState(false);
  const [editing, setEditing] = useState(false);

  const handleRelease = async () => {
    if (!window.confirm("לשחרר ערבות זו?")) return;
    setReleasing(true);
    try {
      await apiClient.delete(`/guarantees/${guarantee._id}`);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.error || "שחרור נכשל");
    } finally {
      setReleasing(false);
    }
  };

  return (
    <>
      <div className="bt-collateral-row">
        <span className="cp-symbol-badge">ערבות</span>
        <span className="bt-collateral-detail">{guaranteeDetailText(guarantee)}</span>
        <div className="bt-collateral-actions">
          {canEdit && (
            <button
              type="button"
              className="ag-grid-action-btn"
              onClick={() => setEditing(true)}
            >
              עריכה
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              className="ag-grid-action-btn ag-grid-action-btn-danger"
              onClick={handleRelease}
              disabled={releasing}
            >
              {releasing ? "..." : "שחרור"}
            </button>
          )}
        </div>
      </div>
      {editing && (
        <GuaranteeEditModal
          guarantee={guarantee}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onRefresh(); }}
        />
      )}
    </>
  );
}

// ─── GuaranteeEditModal ───────────────────────────────────────────────────────

function GuaranteeEditModal({ guarantee, onClose, onSaved }) {
  const [form, setForm] = useState({
    guaranteeType: guarantee.guaranteeType || "PERSONAL",
    guarantorName: guarantee.guarantorName || "",
    amount: guarantee.amount != null ? String(guarantee.amount) : "",
    expiryDate: guarantee.expiryDate ? guarantee.expiryDate.slice(0, 10) : "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        guaranteeType: form.guaranteeType,
        guarantorName: form.guarantorName,
        amount: Number(form.amount),
      };
      payload.expiryDate = form.expiryDate || null; // null clears a previously-set expiry

      await apiClient.put(`/guarantees/${guarantee._id}`, payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="cp-modal-header">
          <h3 className="cp-modal-title">עריכת ערבות</h3>
          <button type="button" onClick={onClose} className="cp-modal-close">×</button>
        </div>
        {error && <div className="cp-error cp-modal-error">{error}</div>}
        <form onSubmit={handleSubmit} className="form-grid cp-modal-form">
          <div className="field">
            <label className="label-required">סוג ערבות</label>
            <select className="select" name="guaranteeType" value={form.guaranteeType} onChange={onChange}>
              {Object.entries(GUARANTEE_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="label-required">שם הערב</label>
            <input className="input" name="guarantorName" value={form.guarantorName} onChange={onChange} placeholder="ישראל ישראלי" required />
          </div>
          <div className="field">
            <label className="label-required">סכום ערבות (₪)</label>
            <input className="input" type="number" name="amount" value={form.amount} onChange={onChange} placeholder="0" required min="0" />
          </div>
          <div className="field">
            <label className="label-optional">תאריך פקיעה</label>
            <input className="input" type="date" name="expiryDate" value={form.expiryDate} onChange={onChange} />
          </div>

          <div className="form-full-width cp-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>ביטול</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "שומר..." : "עדכון"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── AddSecurityModal (create a pledge or a guarantee) ───────────────────────

function AddSecurityModal({ basketId, onClose, onSaved }) {
  const [kind, setKind] = useState("PLEDGE");
  const [holdings, setHoldings] = useState([]);

  const [assetId, setAssetId] = useState("");
  const [pledgedUnits, setPledgedUnits] = useState("");
  const [lienPosition, setLienPosition] = useState("FIRST");
  const [lienHolder, setLienHolder] = useState("");

  const [guaranteeType, setGuaranteeType] = useState("PERSONAL");
  const [guarantorName, setGuarantorName] = useState("");
  const [amount, setAmount] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient.get("/asset-holdings").then((res) => setHoldings(res.data || [])).catch(() => {});
  }, []);

  const selectedAsset = holdings.find((h) => h._id === assetId);
  const isStock = selectedAsset?.assetType === "STOCK";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (kind === "PLEDGE") {
        const payload = {
          assetId,
          basketId,
          lienPosition: lienPosition || undefined,
          lienHolder: lienHolder || undefined,
        };
        if (isStock) payload.pledgedUnits = Number(pledgedUnits);
        await apiClient.post("/pledges", payload);
      } else {
        const payload = {
          basketId,
          guaranteeType,
          guarantorName,
          amount: Number(amount),
        };
        if (expiryDate) payload.expiryDate = expiryDate;
        await apiClient.post("/guarantees", payload);
      }
      onSaved();
    } catch (err) {
      if (isOverPledgedError(err)) {
        window.alert(OVER_PLEDGED_MESSAGE);
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="cp-modal-header">
          <h3 className="cp-modal-title">הוסף בטוחה</h3>
          <button type="button" onClick={onClose} className="cp-modal-close">×</button>
        </div>
        {error && <div className="cp-error cp-modal-error">{error}</div>}
        <form onSubmit={handleSubmit} className="form-grid cp-modal-form">
          <div className="field form-full-width">
            <label className="label-required">סוג בטוחה</label>
            <select className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="PLEDGE">שעבוד נכס</option>
              <option value="GUARANTEE">ערבות</option>
            </select>
          </div>

          {kind === "PLEDGE" && (
            <>
              <div className="field form-full-width">
                <label className="label-required">נכס</label>
                <select
                  className="select"
                  value={assetId}
                  onChange={(e) => { setAssetId(e.target.value); setPledgedUnits(""); }}
                  required
                >
                  <option value="">— בחר נכס —</option>
                  {holdings.map((h) => (
                    <option key={h._id} value={h._id}>
                      {h.assetName}{h.symbol ? ` · ${h.symbol}` : ""}{h.owningCompany ? ` · ${h.owningCompany}` : ""}
                      {h.assetType === "STOCK" ? ` · פנוי: ${formatNum(h.freeUnits)} מניות` : ""}
                    </option>
                  ))}
                </select>
              </div>
              {isStock && (
                <div className="field">
                  <label className="label-required">מניות לשעבוד</label>
                  <input className="input" type="number" value={pledgedUnits} onChange={(e) => setPledgedUnits(e.target.value)} placeholder="0" required min="1" />
                </div>
              )}
              <div className="field">
                <label className="label-optional">דרגת שעבוד</label>
                <select className="select" value={lienPosition} onChange={(e) => setLienPosition(e.target.value)}>
                  {Object.entries(LIEN_POSITION_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="label-optional">גוף משעבד</label>
                <input className="input" value={lienHolder} onChange={(e) => setLienHolder(e.target.value)} placeholder="בנק" />
              </div>
            </>
          )}

          {kind === "GUARANTEE" && (
            <>
              <div className="field">
                <label className="label-required">סוג ערבות</label>
                <select className="select" value={guaranteeType} onChange={(e) => setGuaranteeType(e.target.value)}>
                  {Object.entries(GUARANTEE_TYPE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="label-required">שם הערב</label>
                <input className="input" value={guarantorName} onChange={(e) => setGuarantorName(e.target.value)} placeholder="ישראל ישראלי" required />
              </div>
              <div className="field">
                <label className="label-required">סכום ערבות (₪)</label>
                <input className="input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" required min="0" />
              </div>
              <div className="field">
                <label className="label-optional">תאריך פקיעה</label>
                <input className="input" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
              </div>
            </>
          )}

          <div className="form-full-width cp-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>ביטול</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "שומר..." : "הוסף"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── BasketFormModal ──────────────────────────────────────────────────────────

function BasketFormModal({ basket, onClose, onSaved }) {
  const isNew = !basket?._id;
  const [form, setForm] = useState({
    name: basket?.name || "",
    lender: basket?.lender || "",
    borrowerCompany: basket?.borrowerCompany || "",
    ltvWarningThreshold: basket?.ltvWarningThreshold != null ? String(basket.ltvWarningThreshold) : "",
    ltvBreachThreshold: basket?.ltvBreachThreshold != null ? String(basket.ltvBreachThreshold) : "",
    ltvPriceFormula: basket?.ltvPriceFormula || "SPOT",
    covenantText: basket?.covenantText || "",
    notes: basket?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };
      ["ltvWarningThreshold", "ltvBreachThreshold"].forEach((f) => {
        if (payload[f] !== "" && payload[f] != null) payload[f] = Number(payload[f]);
        else delete payload[f];
      });
      if (!payload.lender) delete payload.lender;
      if (!payload.borrowerCompany) delete payload.borrowerCompany;
      if (!payload.notes) delete payload.notes;
      if (!payload.covenantText) delete payload.covenantText;

      if (basket?._id) {
        await apiClient.put(`/baskets/${basket._id}`, payload);
      } else {
        await apiClient.post(`/baskets`, payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal-box bt-basket-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="cp-modal-header">
          <h3 className="cp-modal-title">{isNew ? "מסגרת חדשה" : `עריכת מסגרת — ${basket.name}`}</h3>
          <button type="button" onClick={onClose} className="cp-modal-close">×</button>
        </div>
        {error && <div className="cp-error cp-modal-error">{error}</div>}
        <form onSubmit={handleSubmit} className="form-grid cp-modal-form">
          <div className="field form-full-width">
            <label className="label-required">שם המסגרת</label>
            <input className="input" name="name" value={form.name} onChange={onChange} placeholder="מסגרת אשראי א׳" required />
          </div>
          <div className="field">
            <label className="label-optional">מלווה</label>
            <input className="input" name="lender" value={form.lender} onChange={onChange} placeholder="בנק לאומי" />
          </div>
          <div className="field">
            <label className="label-optional">חברת לווה</label>
            <input className="input" name="borrowerCompany" value={form.borrowerCompany} onChange={onChange} placeholder="חברת XYZ" />
          </div>
          <div className="field">
            <label className="label-optional">סף אזהרה LTV (%)</label>
            <input className="input" type="number" name="ltvWarningThreshold" value={form.ltvWarningThreshold} onChange={onChange} placeholder="65" min="0" max="200" step="0.1" />
          </div>
          <div className="field">
            <label className="label-optional">סף הפרה LTV (%)</label>
            <input className="input" type="number" name="ltvBreachThreshold" value={form.ltvBreachThreshold} onChange={onChange} placeholder="80" min="0" max="200" step="0.1" />
          </div>
          <div className="field">
            <label className="label-optional">שיטת מחיר</label>
            <select className="select" name="ltvPriceFormula" value={form.ltvPriceFormula} onChange={onChange}>
              {Object.entries(PRICE_FORMULA_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div className="field form-full-width">
            <label className="label-optional">טקסט קובנאנט</label>
            <textarea className="input" name="covenantText" value={form.covenantText} onChange={onChange} rows={2} placeholder="נוסח הקובנאנט..." style={{ resize: "vertical" }} />
          </div>
          <div className="field form-full-width">
            <label className="label-optional">הערות</label>
            <input className="input" name="notes" value={form.notes} onChange={onChange} placeholder="הערות נוספות" />
          </div>

          <div className="form-full-width cp-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>ביטול</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "שומר..." : isNew ? "צור מסגרת" : "עדכון"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── ReviewModal ──────────────────────────────────────────────────────────────

function ReviewModal({ reviewState, onClose, onConfirmLeave, onRefresh, onItemReleased, canEdit, canRemove }) {
  const { basket, mode, loan, pledges = [], guarantees = [] } = reviewState;
  const [localMovePledgeIds, setLocalMovePledgeIds] = useState(reviewState.movePledgeIds || []);
  const [localMoveGuaranteeIds, setLocalMoveGuaranteeIds] = useState(reviewState.moveGuaranteeIds || []);
  const [addingSecurity, setAddingSecurity] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const togglePledgeMove = (id) => {
    setLocalMovePledgeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const toggleGuaranteeMove = (id) => {
    setLocalMoveGuaranteeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirmLeave(localMovePledgeIds, localMoveGuaranteeIds);
    } finally {
      setConfirming(false);
    }
  };

  const isLeave = mode === "leave";
  const isEmpty = pledges.length === 0 && guarantees.length === 0;

  const releaseItem = async (kind, id) => {
    const isLastPledge = kind === "PLEDGE" && pledges.length === 1;
    const msg = kind === "PLEDGE"
      ? (isLastPledge ? LAST_PLEDGE_CONFIRM : "לשחרר שעבוד זה?")
      : "לשחרר ערבות זו?";
    if (!window.confirm(msg)) return;
    try {
      await apiClient.delete(`/${kind === "PLEDGE" ? "pledges" : "guarantees"}/${id}`);
      // Shrink the modal's own snapshot too — the last-pledge warning above is
      // computed from it, so a second in-modal release must see the true count.
      onItemReleased(kind, id);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.error || "שחרור נכשל");
    }
  };

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal-box bt-review-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="cp-modal-header">
          <h3 className="cp-modal-title">
            {isLeave
              ? `הסרת הלוואה ממסגרת "${basket.name}"`
              : `בטוחות מאוחדות — מסגרת "${basket.name}"`}
          </h3>
          <button type="button" onClick={onClose} className="cp-modal-close">×</button>
        </div>

        {isLeave && loan && (
          <div className="bt-review-info">
            <span className="bt-review-info-text">
              הלוואה: <strong>{loan.lender}</strong> / {loan.borrowerCompany}
            </span>
            <p className="bt-review-hint">
              סמן בטוחות שיעברו עם ההלוואה היוצאת. לא מסומן = נשאר במסגרת.
            </p>
          </div>
        )}

        {!isLeave && (
          <div className="bt-review-info">
            <p className="bt-review-hint">
              להלן רשימת הבטוחות המאוחדת לאחר הצטרפות ההלוואה למסגרת. ניתן להוסיף ולהסיר בטוחות.
            </p>
          </div>
        )}

        <div className="bt-review-collateral-list">
          {isEmpty && (
            <div className="cp-empty" style={{ padding: "20px 0" }}>אין בטוחות</div>
          )}

          {pledges.map((p) => (
            <div
              key={p._id}
              className={`bt-review-row ${isLeave && localMovePledgeIds.includes(p._id) ? "bt-review-row-checked" : ""}`}
            >
              {isLeave && (
                <input
                  type="checkbox"
                  checked={localMovePledgeIds.includes(p._id)}
                  onChange={() => togglePledgeMove(p._id)}
                  className="bt-review-checkbox"
                />
              )}
              <span className="cp-symbol-badge">{ASSET_TYPE_LABELS[p.assetId?.assetType] || p.assetId?.assetType || "—"}</span>
              <span className="bt-collateral-detail">{pledgeDetailText(p)}</span>
              {!isLeave && canRemove && (
                <button
                  type="button"
                  className="ag-grid-action-btn ag-grid-action-btn-danger"
                  onClick={() => releaseItem("PLEDGE", p._id)}
                >
                  שחרור
                </button>
              )}
            </div>
          ))}

          {guarantees.map((g) => (
            <div
              key={g._id}
              className={`bt-review-row ${isLeave && localMoveGuaranteeIds.includes(g._id) ? "bt-review-row-checked" : ""}`}
            >
              {isLeave && (
                <input
                  type="checkbox"
                  checked={localMoveGuaranteeIds.includes(g._id)}
                  onChange={() => toggleGuaranteeMove(g._id)}
                  className="bt-review-checkbox"
                />
              )}
              <span className="cp-symbol-badge">ערבות</span>
              <span className="bt-collateral-detail">{guaranteeDetailText(g)}</span>
              {!isLeave && canRemove && (
                <button
                  type="button"
                  className="ag-grid-action-btn ag-grid-action-btn-danger"
                  onClick={() => releaseItem("GUARANTEE", g._id)}
                >
                  שחרור
                </button>
              )}
            </div>
          ))}
        </div>

        {!isLeave && canEdit && (
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setAddingSecurity(true)}
            >
              + הוסף בטוחה
            </button>
          </div>
        )}

        <div className="cp-modal-actions" style={{ marginTop: 20 }}>
          {isLeave ? (
            <>
              <button type="button" className="btn btn-ghost" onClick={onClose} disabled={confirming}>ביטול</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={confirming}
              >
                {confirming ? "מסיר..." : "אשר הסרה"}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary" onClick={onClose}>סגור</button>
          )}
        </div>

        {addingSecurity && (
          <AddSecurityModal
            basketId={basket._id}
            onClose={() => setAddingSecurity(false)}
            onSaved={() => { setAddingSecurity(false); onRefresh(); }}
          />
        )}
      </div>
    </div>
  );
}

// ─── BasketCard ───────────────────────────────────────────────────────────────

function BasketCard({ basket, allLoans, canEdit, canColCreate, canColEdit, canColRemove, onEdit, onDelete, onAddLoan, onRemoveLoan, onRefresh }) {
  const [addingSecurity, setAddingSecurity] = useState(false);
  const ltv = basket.ltv || {};
  const cfg = ltvStatusConfig(ltv.ltvStatus);
  const pledges = basket.pledges || [];
  const guarantees = basket.guarantees || [];

  const availableLoans = useMemo(() => {
    const memberIds = new Set((basket.loans || []).map((l) => String(l._id)));
    return allLoans.filter(
      (l) => !l.basketName && l.status !== "repaid" && !memberIds.has(String(l._id))
    );
  }, [allLoans, basket.loans]);

  const [selectedLoanId, setSelectedLoanId] = useState("");

  const handleAddLoan = async () => {
    if (!selectedLoanId) return;
    try {
      await onAddLoan(basket, selectedLoanId);
      setSelectedLoanId("");
    } catch (e) {
      alert(e.response?.data?.error || "שגיאה בהוספת הלוואה");
    }
  };

  return (
    <div className={`bt-basket-card cp-ltv-card cp-ltv-card-${ltv.ltvStatus || "none"}`}>
      {/* Card header */}
      <div className="bt-basket-card-header">
        <div className="bt-basket-card-identity">
          <span className={`cp-status-badge ${cfg.cls}`}>{cfg.label}</span>
          <div className="bt-basket-name">{basket.name}</div>
          {(basket.lender || basket.borrowerCompany) && (
            <div className="bt-basket-meta">
              {basket.lender && <span>{basket.lender}</span>}
              {basket.lender && basket.borrowerCompany && <span> / </span>}
              {basket.borrowerCompany && <span>{basket.borrowerCompany}</span>}
            </div>
          )}
        </div>

        {/* LTV summary */}
        <div className="bt-basket-ltv-area">
          <div className="bt-ltv-row">
            <span className="cp-ltv-summary-label">LTV נוכחי</span>
            <MiniGauge ltv={ltv.currentLTV} warning={basket.ltvWarningThreshold} breach={basket.ltvBreachThreshold} />
          </div>
          <div className="bt-ltv-row">
            <span className="cp-ltv-summary-label">חוב נטו</span>
            <span className="cp-ltv-summary-value">{formatILS(ltv.netDebt, true)}</span>
          </div>
          <div className="bt-ltv-row">
            <span className="cp-ltv-summary-label">שווי בטוחה</span>
            <span className="cp-ltv-summary-value">{formatILS(ltv.collateralValue, true)}</span>
          </div>
          {ltv.guaranteeTotal > 0 && (
            <div className="bt-ltv-row">
              <span className="cp-ltv-summary-label">ערבויות</span>
              <span className="cp-ltv-summary-value">{formatILS(ltv.guaranteeTotal, true)}</span>
            </div>
          )}
          {basket.ltvWarningThreshold && (
            <div className="bt-ltv-row">
              <span className="cp-ltv-summary-label">סף אזהרה</span>
              <span className="cp-threshold-cell warning">{formatPct(basket.ltvWarningThreshold)}</span>
            </div>
          )}
          {basket.ltvBreachThreshold && (
            <div className="bt-ltv-row">
              <span className="cp-ltv-summary-label">סף הפרה</span>
              <span className="cp-threshold-cell breach">{formatPct(basket.ltvBreachThreshold)}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="bt-basket-card-actions">
            <button type="button" className="ag-grid-action-btn" onClick={() => onEdit(basket)}>
              עריכה
            </button>
            <button
              type="button"
              className="ag-grid-action-btn ag-grid-action-btn-danger"
              onClick={() => onDelete(basket)}
            >
              מחיקה
            </button>
          </div>
        )}
      </div>

      {/* Member loans */}
      <div className="bt-section">
        <div className="cp-section-divider" style={{ marginTop: 0 }}>הלוואות במסגרת ({(basket.loans || []).length})</div>
        {(basket.loans || []).length === 0 && (
          <div className="bt-empty-hint">אין הלוואות במסגרת</div>
        )}
        {(basket.loans || []).map((loan) => (
          <div key={loan._id} className="bt-loan-row">
            <div className="bt-loan-identity">
              <span className="bt-loan-lender">{loan.lender || "—"}</span>
              {loan.borrowerCompany && <span className="bt-loan-borrower">{loan.borrowerCompany}</span>}
              {loan.remainingPrincipal != null && (
                <span className="bt-loan-amount">{formatILS(loan.remainingPrincipal, true)}</span>
              )}
            </div>
            {canEdit && (
              <button
                type="button"
                className="ag-grid-action-btn"
                onClick={() => onRemoveLoan(basket, loan)}
              >
                הסר מהמסגרת
              </button>
            )}
          </div>
        ))}

        {/* Add loan */}
        {canEdit && availableLoans.length > 0 && (
          <div className="bt-add-loan-row">
            <select
              className="select bt-loan-select"
              value={selectedLoanId}
              onChange={(e) => setSelectedLoanId(e.target.value)}
            >
              <option value="">— בחר הלוואה להוספה —</option>
              {availableLoans.map((l) => (
                <option key={l._id} value={l._id}>
                  {l.lender || "?"} / {l.borrowerCompany || "?"}{l.remainingPrincipal ? ` · ${formatILS(l.remainingPrincipal, true)}` : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleAddLoan}
              disabled={!selectedLoanId}
            >
              + הוסף הלוואה
            </button>
          </div>
        )}
      </div>

      {/* Pledges + Guarantees */}
      <div className="bt-section">
        <div className="cp-section-divider" style={{ marginTop: 0 }}>
          בטוחות ({pledges.length + guarantees.length})
          {canColCreate && (
            <button
              type="button"
              className="ag-grid-action-btn"
              style={{ marginRight: 12 }}
              onClick={() => setAddingSecurity(true)}
            >
              + הוסף בטוחה
            </button>
          )}
        </div>
        {pledges.length === 0 && guarantees.length === 0 && (
          <div className="bt-empty-hint">אין בטוחות במסגרת</div>
        )}
        {pledges.map((p) => (
          <PledgeRow
            key={p._id}
            pledge={p}
            canEdit={canColEdit}
            canRemove={canColRemove}
            isLastActive={pledges.length === 1}
            onRefresh={onRefresh}
          />
        ))}
        {guarantees.map((g) => (
          <GuaranteeRow
            key={g._id}
            guarantee={g}
            canEdit={canColEdit}
            canRemove={canColRemove}
            onRefresh={onRefresh}
          />
        ))}
      </div>

      {basket.notes && (
        <div className="cp-holding-reason" style={{ marginTop: 8 }}>
          {basket.notes}
        </div>
      )}

      {addingSecurity && (
        <AddSecurityModal
          basketId={basket._id}
          onClose={() => setAddingSecurity(false)}
          onSaved={() => { setAddingSecurity(false); onRefresh(); }}
        />
      )}
    </div>
  );
}

// ─── BasketsTab (Main) ────────────────────────────────────────────────────────

export default function BasketsTab() {
  const user = useCurrentUser();
  const canEdit = can(user, "collateral.edit");
  const canColCreate = can(user, "collateral.create");
  const canColEdit = can(user, "collateral.edit");
  const canColRemove = can(user, "collateral.remove");

  const [baskets, setBaskets] = useState([]);
  const [allLoans, setAllLoans] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // basket being created/edited, null=closed
  const [reviewState, setReviewState] = useState(null); // { basket, mode, loan, pledges, guarantees, movePledgeIds, moveGuaranteeIds }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [bRes, lRes] = await Promise.all([
        apiClient.get("/baskets", {
          params: search.trim() ? { search: search.trim() } : {},
        }),
        apiClient.get("/loans"),
      ]);
      setBaskets(bRes.data || []);
      setAllLoans(lRes.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  // ── actions ──────────────────────────────────────────────────────────────────

  const handleSaveBasket = async (form) => {
    await refresh();
  };

  const deleteBasket = async (basket) => {
    if (!window.confirm(`למחוק את המסגרת "${basket.name}"? (חייבת להיות ריקה מהלוואות)`)) return;
    try {
      await apiClient.delete(`/baskets/${basket._id}`);
      refresh();
    } catch (e) {
      alert(e.response?.data?.error || "מחיקה נכשלה");
    }
  };

  /** Fresh ACTIVE pledges/guarantees for one basket (avoids stale render-snapshot data). */
  const fetchBasketSecurity = async (basketId) => {
    const res = await apiClient.get(`/baskets/${basketId}`);
    return { pledges: res.data.pledges || [], guarantees: res.data.guarantees || [] };
  };

  const addLoan = async (basket, loanId) => {
    await apiClient.post(`/baskets/${basket._id}/loans`, { loanId });
    // refresh the basket list first, then open the security review seeded from a fresh
    // GET /baskets/:id (authoritative merged pledges/guarantees post-singleton-merge)
    await refresh();
    const security = await fetchBasketSecurity(basket._id).catch(() => ({ pledges: [], guarantees: [] }));
    setReviewState({
      basket,
      mode: "join",
      pledges: security.pledges,
      guarantees: security.guarantees,
      movePledgeIds: [],
      moveGuaranteeIds: [],
    });
  };

  const removeLoan = async (basket, loan) => {
    // fetch fresh pledges/guarantees so the leave-review modal is not stale;
    // fall back to the render-snapshot basket if the refetch fails
    const fallback = { pledges: basket.pledges || [], guarantees: basket.guarantees || [] };
    const security = await fetchBasketSecurity(basket._id).catch(() => fallback);
    setReviewState({
      basket,
      mode: "leave",
      loan,
      pledges: security.pledges,
      guarantees: security.guarantees,
      movePledgeIds: [],
      moveGuaranteeIds: [],
    });
  };

  const confirmLeave = async (movePledgeIds, moveGuaranteeIds) => {
    const { basket, loan } = reviewState;
    await apiClient.delete(`/baskets/${basket._id}/loans/${loan._id}`, {
      data: { moveCollateralIds: [], movePledgeIds, moveGuaranteeIds },
    });
    setReviewState(null);
    refresh();
  };

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <div className="bt-baskets-tab">
      {/* Toolbar */}
      <div className="bt-toolbar">
        <input
          className="input bt-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חיפוש מסגרות..."
        />
        {canEdit && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() =>
              setEditing({
                name: "",
                lender: "",
                borrowerCompany: "",
                ltvWarningThreshold: "",
                ltvBreachThreshold: "",
                ltvPriceFormula: "SPOT",
                covenantText: "",
                notes: "",
              })
            }
          >
            + מסגרת חדשה
          </button>
        )}
      </div>

      {error && <div className="cp-error">{error}</div>}

      {loading ? (
        <div className="cp-loading">טוען מסגרות...</div>
      ) : baskets.length === 0 ? (
        <div className="cp-empty-state">
          <div className="cp-empty-icon">🗂</div>
          <div className="cp-empty-title">
            {search.trim() ? "לא נמצאו מסגרות התואמות לחיפוש" : "אין מסגרות מוגדרות"}
          </div>
          {!search.trim() && (
            <div className="cp-empty-body">
              מסגרות מאגדות מספר הלוואות לצורך חישוב LTV משותף וניהול בטוחות מאוחד.
            </div>
          )}
          {canEdit && !search.trim() && (
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: 16 }}
              onClick={() =>
                setEditing({
                  name: "",
                  lender: "",
                  borrowerCompany: "",
                  ltvWarningThreshold: "",
                  ltvBreachThreshold: "",
                  ltvPriceFormula: "SPOT",
                  covenantText: "",
                  notes: "",
                })
              }
            >
              + צור מסגרת ראשונה
            </button>
          )}
        </div>
      ) : (
        <div className="bt-basket-list">
          {baskets.map((basket) => (
            <BasketCard
              key={basket._id}
              basket={basket}
              allLoans={allLoans}
              canEdit={canEdit}
              canColCreate={canColCreate}
              canColEdit={canColEdit}
              canColRemove={canColRemove}
              onEdit={(b) => setEditing(b)}
              onDelete={deleteBasket}
              onAddLoan={addLoan}
              onRemoveLoan={removeLoan}
              onRefresh={refresh}
            />
          ))}
        </div>
      )}

      {/* Basket create/edit modal */}
      {editing !== null && (
        <BasketFormModal
          basket={editing._id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            handleSaveBasket();
          }}
        />
      )}

      {/* Join / Leave review modal */}
      {reviewState && (
        <ReviewModal
          reviewState={reviewState}
          onClose={() => setReviewState(null)}
          onConfirmLeave={confirmLeave}
          onRefresh={refresh}
          onItemReleased={(kind, id) =>
            setReviewState((s) => s && ({
              ...s,
              pledges: kind === "PLEDGE" ? (s.pledges || []).filter((p) => p._id !== id) : s.pledges,
              guarantees: kind === "GUARANTEE" ? (s.guarantees || []).filter((g) => g._id !== id) : s.guarantees,
            }))
          }
          canEdit={canColEdit}
          canRemove={canColRemove}
        />
      )}
    </div>
  );
}
